// src/components/dashboard/EventDetails/EventDetails.jsx
// 🔧 重构后的 EventDetails 主组件 - 添加天气预警功能

import React from "react";
import { Crosshair, Info, Cloud, AlertTriangle } from "lucide-react";
import Card from "../../ui/Card/Card";
import EventList from "./EventList";
import { useNWSAlerts } from "../../../services/nwsWeatherService"; // 🔧 新增
import styles from "./EventDetails.module.css";

const EventDetails = ({
  selectedSensor,
  selectedEvent,
  allSensors,
  onSensorSelect,
}) => {
  // 🔧 新增：获取天气预警数据
  const { alerts, riskLevel, riskLevelInfo, isLoading: alertsLoading } = useNWSAlerts();

  // 空状态组件
  const EmptyState = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyStateIcon}>
        <Crosshair className="h-8 w-8 text-gray-400" />
      </div>
      <p className={styles.emptyStateTitle}>
        Select a sensor for regional monitoring
      </p>
      <p className={styles.emptyStateDescription}>
        Click any sensor to view flood events within 3km range
      </p>
    </div>
  );

  // 🔧 新增：天气预警状态组件
  const WeatherAlertStatus = () => {
    if (alertsLoading) {
      return (
        <div className="mb-3 border border-gray-200 bg-gray-50 rounded-lg p-3 flex items-center gap-3">
          <div className="flex-shrink-0">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
          </div>
          <div className="text-sm text-gray-600">
            Checking weather alerts...
          </div>
        </div>
      );
    }

    const alertCount = alerts.length;
    const hasActiveAlerts = alertCount > 0;

    if (!hasActiveAlerts) {
      // 无预警状态
      return (
        <div className="mb-3 border border-green-200 bg-green-50 rounded-lg p-3 flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-5 h-5 text-green-600 mt-0.5">
              {riskLevelInfo.icon}
            </div>
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium text-green-800">
              {riskLevelInfo.title}
            </div>
            <div className="text-xs text-green-700 mt-1">
              {riskLevelInfo.message}
            </div>
          </div>
        </div>
      );
    }

    // 有预警状态
    const borderColor = riskLevel === 'emergency' ? 'border-red-500' : 
                       riskLevel === 'warning' ? 'border-orange-500' : 'border-yellow-500';
    const bgColor = riskLevel === 'emergency' ? 'bg-red-50' : 
                   riskLevel === 'warning' ? 'bg-orange-50' : 'bg-yellow-50';

    return (
      <div className={`mb-3 border ${borderColor} ${bgColor} rounded-lg p-3`}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <div className="w-5 h-5 mt-0.5" style={{ color: riskLevelInfo.color }}>
              {riskLevelInfo.icon}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium" style={{ color: riskLevelInfo.color }}>
              {riskLevelInfo.title}
              {alertCount > 1 && (
                <span className="text-xs ml-2 opacity-75">
                  ({alertCount} alerts)
                </span>
              )}
            </div>
            <div className="text-xs mt-1" style={{ color: riskLevelInfo.color, opacity: 0.9 }}>
              {riskLevelInfo.message}
            </div>
            
            {/* 显示最重要的警报详情 */}
            {alerts.length > 0 && (
              <div className="text-xs mt-2 opacity-80" style={{ color: riskLevelInfo.color }}>
                <div className="font-medium">{alerts[0].event}</div>
                <div>{alerts[0].userMessage}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // 事件基本信息组件（带"无实时洪水"状态提示）
  const EventInfo = ({ sensor, event }) => (
    <div className={styles.eventCardContent}>
      {/* 🔧 新增：天气预警状态 */}
      <WeatherAlertStatus />

      {/* 地点信息 */}
      <div className={styles.eventCardHeader}>
        <div className={styles.eventLocation}>
          <h3 className={styles.locationName}>📍 {sensor.location}</h3>
          <p className={styles.locationBorough}>{sensor.borough}</p>
        </div>
      </div>

      {/* 当前无洪水提示 */}
      <div className="mt-3 border border-green-200 bg-green-50 rounded-lg p-3 flex items-start gap-3">
        <div className="flex-shrink-0">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-green-600 mt-0.5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 10-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="text-sm text-green-800">
          <div className="font-medium mb-0.5">✅ No Active Flooding</div>
          <div className="text-xs text-green-700 opacity-90">
            The system checks all nearby sensors every minute. No ongoing flood activity was detected in this area.
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Card className={styles.eventDetailsContainer}>
      {/* Header：区域监控标题与说明 */}
      <Card.Header>
        <div className="space-y-2">
          <h2 className={styles.eventDetailsTitle}>
            <Crosshair className={styles.titleIcon} />
            Regional Monitor
            <span className="text-sm font-normal text-gray-500 ml-2">
              3km Range
            </span>
          </h2>

          {/* 区域监控说明 */}
          {selectedSensor && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-800">
                  <div className="font-medium mb-1">
                    📡 Regional Flood Monitoring
                  </div>
                  <div>
                    Monitoring flood events from{" "}
                    <strong>all sensors within 3km</strong> of your selected
                    location. This includes multiple sensors in the area, not
                    just the selected one.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card.Header>

      <Card.Body className={styles.eventDetailsBody}>
        {/* 上半部分 sensor info */}
        <div className={styles.eventInfoSection}>
          {selectedSensor ? (
            <EventInfo sensor={selectedSensor} event={selectedEvent} />
          ) : (
            <EmptyState />
          )}
        </div>

        {/* 下半部分 recent events */}
        <div className={styles.eventListSection}>
          <EventList
            allSensors={allSensors}
            selectedSensor={selectedSensor}
            onSensorSelect={onSensorSelect}
            maxItems={8}
            showFilters={false}
          />
        </div>
      </Card.Body>
    </Card>
  );
};

export default EventDetails;